import Checkbox from "@/components/Checkbox";
import { sortBy, uniq } from "@/lib/array";
import classNames from "classnames";
import PlusCircleIcon from "./icons/PlusCircleIcon";
import MinusCircleIcon from "./icons/MinusCircleIcon";
import { useState } from "react";
import PlusIcon from "./icons/PlusIcon";
import useDeviceInfo from "@/src/useDeviceInfo";


export default function TopicPicker({topics, readonly, onChange, compact, allowNew, children}) {

  const { mobile } = useDeviceInfo()

  const selectedTopicIds = topics.filter(t => t.enabled).map(t => t.id)

  // Group the topics by group name
  const groupNames = uniq(topics.map(t => t.groupName))
  const groups = groupNames.map(name => ({
    id:     name,
    name:   name,
    items:  [],
  }))

  // Multiple topics may have the same display name, so we need to group them
  // together under a single UI item,
  topics.forEach(topic => {
    const group = groups.find(g => g.name === topic.groupName)
    let item = group.items.find(i => i.name === topic.displayName)

    if (!item) {
      item = {
        id:     topic.id,
        name:   topic.displayName,
        topics: [],
      }
      group.items.push(item)

      if (topic.catalogId) {
        group.isCatalog = true
      }
    }

    item.topics.push(topic)
  })

  const wideLayout = groups.length > 1 && !compact


  function isSelected (topic) {
    return topics.find(t => t.id === topic.id)?.enabled
  }


  function canAddTopics (group) {
    return !group.isCatalog && !readonly && allowNew
  }


  function toggleTopic (topic) {
    onChange(topics => {
      return topics.map(t => t.id === topic.id ? {...t, enabled: !t.enabled} : t)
    })
  }


  function selectGroup(group, selected) {
    const selectedIds = group.items.map(i => i.id)

    onChange(topics => {
      return topics.map(t => {
        if (selectedIds.includes(t.id)) {
          return {...t, enabled: selected}
        }
        return t
      })
    })
  }


  return <>
    <div className="px-2 bg-f3f3f3 text-center">
      <div className="content mx-2">
        { (readonly && !selectedTopicIds.length) &&
          <p className="gray">
            No topics selected.
          </p>
        }

        <div className={classNames("column-layout py-3", {'wide-layout': wideLayout})}>
          { groups.map(group => {
            const allSelected = group.items.every(isSelected)
            const noneSelected = group.items.every(c => !isSelected(c))
            const longNames = group.items.find(item => item.name.length > 30)

            if (readonly && noneSelected) return null

            return (
              <div className="avoid-column-break" key={group.id}>

                <div className="text-left category-group bg-white border border-gray px-4 py-1 rounded-lg shadow mb-4">
                  <div className="flex-row spread border-bottom my-1 pb-1">
                    <div className={"gray italics py-1"}>
                      {group.name}
                    </div>
                    { !readonly &&
                      <div className="text-sm flex-row gap-1">
                        <div title="Select All" href="#" className={classNames({link: !allSelected, pointer: !allSelected, "light-gray": allSelected, "cursor-not-allowed": allSelected})} onClick={() => selectGroup(group, true)}>
                          <PlusCircleIcon/>
                        </div>
                        <div title="Select None" href="#" className={classNames({link: !noneSelected, pointer: !noneSelected, "light-gray": noneSelected, "cursor-not-allowed": noneSelected})} onClick={() => selectGroup(group, false)}>
                          <MinusCircleIcon/>
                        </div>
                      </div>
                    }
                  </div>

                  <div className={classNames('py-1', {'two-column': !longNames})}>
                    { sortBy(group.items, 'displayName').map(item => {
                      const checked = isSelected(item)
                      if (readonly && !checked) return null
                      if (readonly) {
                        return (
                          <div key={item.id} className="px-1 flex-row flex-align-start gap-1 mb-2">
                            <span className="">•</span>
                            <span>{item.name}</span>
                          </div>
                        )
                      }
                      return (
                        <div className={classNames("avoid-column-break topic-checkbox")} key={item.id}>
                          <Checkbox
                            alignTop
                            checked={checked}
                            onChange={() => toggleTopic(item)}
                          >
                            <span className={classNames({ gray: !checked, strike: !checked})}>
                              {item.name}
                            </span>
                          </Checkbox>
                          <div className="topic-ids tiny gray">
                            {JSON.stringify(item.topics)}
                          </div>
                        </div>
                      )
                    })}
                  </div>

                  { canAddTopics(group) &&
                    <div className="py-2">
                      <NewItemUI
                        groupName={mobile ? 'items' : (group.name || '').toLowerCase() || 'items'}
                        onAdd={itemName => {
                          onChange(topics => {
                            const newTopic = {
                              id:          `new_${new Date().getTime()}`,
                              displayName: itemName,
                              groupName:   group.name,
                              enabled:     true,
                            }
                            return [...topics, newTopic]
                          })
                        }}
                      />
                    </div>
                  }
                </div>
              </div>
            )
          })}

          { children }

        </div>

      </div>
    </div>

    <style jsx>{`
      @media screen and ( min-width: 1200px ) {
        .wide-layout {
          width: 90vw;
        }
      }
      .column-layout {
        column-width: 30rem;
        gap: 1em;
        text-align: left;
      }
      .two-column {
        column-count: 2;
        column-width: 12rem;
      }
      .avoid-column-break {
        break-inside: avoid;
      }
      .topic-checkbox {
        padding: 0.25rem 0;
      }
      .topic-ids {
        display: none;
      }
    `}</style>

  </>
}


function NewItemUI({onAdd, groupName}) {

  const [itemName, setItemName] = useState('')
  const nameEmpty = itemName.trim().length === 0


  function addItem (e) {
    e.preventDefault()
    if (!itemName.trim()) return
    onAdd(itemName)
    setItemName('')
  }

  function handleKeyDown (e) {
    if (e.key === 'Enter') {
      e.preventDefault()
      addItem(e)
    }
  }

  return <>
    <div className="flex-row gap-1">
      <input
        className="input"
        type="text"
        value={itemName}
        onChange={e => setItemName(e.currentTarget.value)}
        placeholder={`Add more ${groupName || 'items'} here`}
        onKeyDown={handleKeyDown}
      />
      <button
        className={classNames("button", {'is-primary': !nameEmpty})}
        style={{padding: '0.5rem'}}
        onClick={addItem}
        disabled={nameEmpty}
      >
        <PlusIcon/>
      </button>
    </div>
  </>


}

